/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   ReadinessView.tsx
 *     Description     :   ReadinessView.tsx — the Backup-Health "Readiness" tab (Spec 236 US3).
 *                         Reads the predicted-problems list + fleet summary from dbo (never probes Azure) and
 *                         shows a StatCard row, a DataTable of entities predicted to FAIL or produce a FALSE
 *                         result on their next backup, and a per-entity drawer with the per-check breakdown.
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-10
 *     Modified        :   2026-10-30 | Peter Dobler | Spec 236 T053: standard header (Principle XXXVII)
 * =========================================================================================
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, DataTable, StatCard, Drawer, Badge, LoadingState, ErrorState, EmptyState } from '@/components/ui';
import { ShieldAlert, AlertTriangle, HelpCircle, CheckCircle2 } from 'lucide-react';
import { ReadinessBadge } from './ReadinessBadge';
import { backupReadinessApi, type ReadinessProblem, type ReadinessStatus } from '../../api/backupReadiness';
import { formatEastern } from '@/utils/dateFormatter';

function fmtWhen(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : formatEastern(v);
}

const checkVariant: Record<string, 'danger' | 'warning' | 'info' | 'default'> = {
  fail: 'danger',
  warn: 'warning',
  unknown: 'info',
};

const EntityDrawer: React.FC<{ target: ReadinessProblem; onClose: () => void }> = ({ target, onClose }) => {
  const detailQ = useQuery({
    queryKey: ['backup', 'health', 'readiness', target.entityType, target.entityId],
    queryFn: () => backupReadinessApi.entity(target.entityType, target.entityId),
  });

  return (
    <Drawer isOpen onClose={onClose} width="lg" title={`Readiness — ${target.entityName ?? target.entityType}`}>
      {detailQ.isLoading ? (
        <LoadingState rows={5} />
      ) : detailQ.isError ? (
        <ErrorState onRetry={() => detailQ.refetch()} />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <ReadinessBadge status={detailQ.data?.status} />
            <span className="text-sm text-[var(--color-text-secondary)]">
              {target.entityType} · evaluated {fmtWhen(detailQ.data?.lastEvaluatedAt ?? null)} · source {detailQ.data?.checkSource}
            </span>
          </div>
          {target.remediation ? (
            <p className="text-sm text-[var(--color-text-primary)]">{target.remediation}</p>
          ) : null}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">Checks</h4>
            {(detailQ.data?.checks ?? []).length === 0 ? (
              <p className="text-sm text-[var(--color-text-secondary)]">No non-passing checks recorded.</p>
            ) : (
              <ul className="space-y-2">
                {(detailQ.data?.checks ?? []).map((c) => (
                  <li key={c.checkCode} className="rounded border border-[var(--color-border)] p-3">
                    <div className="flex items-center gap-2">
                      <Badge variant={checkVariant[c.result] ?? 'default'} size="xs">{c.result}</Badge>
                      <span className="font-medium text-[var(--color-text-primary)]">{c.title ?? c.checkCode}</span>
                    </div>
                    {c.detail ? <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{c.detail}</p> : null}
                    {c.remediation ? (
                      <p className="mt-1 text-xs text-[var(--color-text-secondary)]">Remediation: {c.remediation}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
};

export const ReadinessView: React.FC = () => {
  const summaryQ = useQuery({ queryKey: ['backup', 'health', 'readiness', 'summary'], queryFn: backupReadinessApi.summary });
  const listQ = useQuery({ queryKey: ['backup', 'health', 'readiness', 'list'], queryFn: () => backupReadinessApi.list({ pageSize: 100 }) });
  const [target, setTarget] = useState<ReadinessProblem | null>(null);

  const s = summaryQ.data;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard
          label="Will fail" value={s ? String(s.willFail) : '—'}
          valueClassName={s && s.willFail > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}
          subtext="predicted blocked" icon={<ShieldAlert className="h-4 w-4" />}
        />
        <StatCard
          label="At risk" value={s ? String(s.atRisk) : '—'}
          valueClassName={s && s.atRisk > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}
          subtext="warn-and-run" icon={<AlertTriangle className="h-4 w-4" />}
        />
        <StatCard
          label="Unknown" value={s ? String(s.unknown) : '—'}
          valueClassName={s && s.unknown > 0 ? 'text-cyan-600 dark:text-cyan-400' : undefined}
          subtext="can't verify (stale)" icon={<HelpCircle className="h-4 w-4" />}
        />
        <StatCard
          label="Evaluated" value={s ? `${s.evaluatedEntities} / ${s.enrolledEntities}` : '—'}
          subtext={`last pass ${fmtWhen(s?.lastPassAt ?? null)}`} icon={<CheckCircle2 className="h-4 w-4" />}
        />
      </div>

      {listQ.isLoading ? (
        <LoadingState rows={6} />
      ) : listQ.isError ? (
        <ErrorState onRetry={() => listQ.refetch()} />
      ) : (listQ.data?.rows.length ?? 0) === 0 ? (
        <EmptyState
          icon={<CheckCircle2 className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />}
          title="No predicted backup problems"
          message="No protected entity is predicted to fail or produce a false backup on its next run. (Entities that have never been backed up appear on the Problems tab.)"
        />
      ) : (
        <Card padding="none">
          <DataTable<ReadinessProblem>
            minWidth="56rem"
            columns={[
              {
                key: 'entityName', header: 'Asset', sortable: true, sortValue: (p) => p.entityName ?? '',
                render: (p) => (
                  <span className="flex items-center gap-2">
                    <Badge variant="default" size="xs">{p.entityType}</Badge>
                    <span className="truncate font-medium text-[var(--color-text-primary)]">{p.entityName ?? '—'}</span>
                  </span>
                ),
              },
              {
                key: 'status', header: 'Status', width: '10rem', sortValue: (p) => p.severityRank,
                render: (p) => <ReadinessBadge status={p.status as ReadinessStatus} />,
              },
              { key: 'title', header: 'Predicted problem', render: (p) => p.title ?? p.primaryReasonCode ?? '—' },
              {
                key: 'lastEvaluatedAt', header: 'Evaluated', width: '12rem',
                sortValue: (p) => p.lastEvaluatedAt ?? '', render: (p) => fmtWhen(p.lastEvaluatedAt),
              },
            ]}
            rows={listQ.data?.rows ?? []}
            rowKey={(p) => `${p.entityType}:${p.entityId}`}
            onRowClick={(p) => setTarget(p)}
          />
        </Card>
      )}

      {target ? <EntityDrawer target={target} onClose={() => setTarget(null)} /> : null}
    </div>
  );
};

export default ReadinessView;
